import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import MetricCard from './components/MetricCard';
import ProcessingChart from './components/ProcessingChart';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import DateRangeFilter from './components/DateRangeFilter';
import { format, subDays } from 'date-fns';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, currentOrgId, isSuperadmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isCleanupRunning, setIsCleanupRunning] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [metrics, setMetrics] = useState({
    totalUploads: {
      value: "0",
      title: "Total Uploads",
      subtitle: "This month",
      trend: null,
      trendValue: null,
      icon: "Upload",
      color: "primary"
    },
    passRate: {
      value: "0%",
      title: "Processing Pass Rate",
      subtitle: "Successful extractions",
      trend: null,
      trendValue: null,
      icon: "CheckCircle",
      color: "success"
    },
    avgConfidence: {
      value: "0%",
      title: "Average Confidence",
      subtitle: "AI extraction accuracy",
      trend: null,
      trendValue: null,
      icon: "Target",
      color: "primary"
    },
    timeSaved: {
      value: "0h",
      title: "Time Saved",
      subtitle: "vs manual entry",
      trend: null,
      trendValue: null,
      icon: "Clock",
      color: "success"
    }
  });
  const [processingData, setProcessingData] = useState([]);
  const [confidenceData, setConfidenceData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pendingReviews, setPendingReviews] = useState(0);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!user?.id || !currentOrgId) return;

    setLoading(true);
    try {
      // Set default date range to this week
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      // normalize to day bounds to ensure inclusive filtering
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      if (!dateRange) {
        setDateRange({ from: weekStart, to: weekEnd });
      }

      const startDate = dateRange?.from || weekStart;
      const endDate = dateRange?.to || weekEnd;

      // Fetch invoices for the date range (org scoped)
      const { data: invoices, error: invoicesError } = await supabase
        .from('parsed_invoices')
        .select('*')
        .eq('org_id', currentOrgId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (invoicesError) throw invoicesError;

      const invoiceList = invoices || [];

      // Count uploads from documents within the same window (more accurate for the "Total Uploads" card)
      const { count: docsCount, error: docsErr } = await supabase
        .from('documents')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', currentOrgId)
        .gte('uploaded_at', startDate.toISOString())
        .lte('uploaded_at', endDate.toISOString());
      if (docsErr) throw docsErr;

      // Calculate metrics
      const totalUploads = docsCount || 0;
      const successfulExtractions = invoiceList.filter(
        inv => inv.status !== 'needs_review'
      ).length;
      const passRate = totalUploads > 0
        ? ((successfulExtractions / totalUploads) * 100).toFixed(1)
        : 0;

      const avgConfidence = invoiceList.length > 0
        ? (invoiceList.reduce((sum, inv) => sum + (inv.confidence || 0), 0) / invoiceList.length * 100).toFixed(1)
        : 0;

      // Estimate time saved (2 minutes per invoice)
      const timeSavedMinutes = totalUploads * 2;
      const timeSavedHours = (timeSavedMinutes / 60).toFixed(1);

      // Update metrics
      setMetrics({
        totalUploads: {
          value: totalUploads.toString(),
          title: "Total Uploads",
          subtitle: "This period",
          trend: null,
          trendValue: null,
          icon: "Upload",
          color: "primary"
        },
        passRate: {
          value: `${passRate}%`,
          title: "Processing Pass Rate",
          subtitle: "Successful extractions",
          trend: passRate >= 90 ? "up" : null,
          trendValue: null,
          icon: "CheckCircle",
          color: "success"
        },
        avgConfidence: {
          value: `${avgConfidence}%`,
          title: "Average Confidence",
          subtitle: "AI extraction accuracy",
          trend: avgConfidence >= 85 ? "up" : null,
          trendValue: null,
          icon: "Target",
          color: "primary"
        },
        timeSaved: {
          value: `${timeSavedHours}h`,
          title: "Time Saved",
          subtitle: "vs manual entry",
          trend: "up",
          trendValue: null,
          icon: "Clock",
          color: "success"
        }
      });

      // Generate processing volume data (last 7 days)
      const processData = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(new Date(), i);
        const dateStr = format(date, 'yyyy-MM-dd');
        const count = invoiceList.filter(inv =>
          format(new Date(inv.created_at), 'yyyy-MM-dd') === dateStr
        ).length;
        processData.push({ date: dateStr, invoices: count });
      }
      setProcessingData(processData);

      // Generate confidence trend data (last 7 days)
      const confData = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(new Date(), i);
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayInvoices = invoiceList.filter(inv =>
          format(new Date(inv.created_at), 'yyyy-MM-dd') === dateStr
        );
        const avgConf = dayInvoices.length > 0
          ? dayInvoices.reduce((sum, inv) => sum + (inv.confidence || 0), 0) / dayInvoices.length * 100
          : 0;
        confData.push({ date: dateStr, confidence: avgConf });
      }
      setConfidenceData(confData);

      // Fetch recent activities from audit log
      const { data: auditData, error: auditError } = await supabase
        .from('audit_log')
        .select('*')
        .eq('org_id', currentOrgId)
        .order('timestamp', { ascending: false })
        .limit(10);

      if (auditError) throw auditError;

      const formattedActivities = (auditData || []).map(log => ({
        id: log.id,
        type: log.action,
        message: `${log.action} ${log.entity_type} ${log.entity_id}`,
        timestamp: new Date(log.timestamp),
        user: user.email || 'User',
        details: log.meta_json ? JSON.stringify(log.meta_json) : null
      }));

      setActivities(formattedActivities);

      // Count pending reviews
      const { count, error: countError } = await supabase
        .from('parsed_invoices')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', currentOrgId)
        .eq('status', 'needs_review');

      if (countError) throw countError;
      setPendingReviews(count || 0);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user?.id, currentOrgId, dateRange]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleCleanupData = async () => {
    if (isCleanupRunning || !user?.id || !currentOrgId) return;

    const confirmed = window.confirm(
      'This will permanently delete ALL uploaded PDFs, invoices, and processed records for this account. This cannot be undone. Continue?'
    );

    if (!confirmed) return;

    setIsCleanupRunning(true);
    try {
      const { data: invoiceIds, error: invoiceIdError } = await supabase
        .from('parsed_invoices')
        .select('id')
        .eq('org_id', currentOrgId);
      if (invoiceIdError) throw invoiceIdError;

      const invoiceIdList = (invoiceIds || []).map(inv => inv.id);

      if (invoiceIdList.length > 0) {
        const { error: rawError } = await supabase
          .from('raw_extracted_data')
          .delete()
          .in('invoice_id', invoiceIdList);
        if (rawError) throw rawError;
      }

      const { error: invoiceError } = await supabase
        .from('parsed_invoices')
        .delete()
        .eq('org_id', currentOrgId);
      if (invoiceError) throw invoiceError;

      const { data: documents, error: docError } = await supabase
        .from('documents')
        .select('id, storage_path')
        .eq('org_id', currentOrgId);
      if (docError) throw docError;

      if (documents?.length) {
        const paths = documents.map(doc => doc.storage_path).filter(Boolean);
        if (paths.length) {
        const { error: storageError } = await supabase.storage
          .from('invoice-documents')
          .remove(paths);
        if (storageError) console.warn('Some storage files could not be removed:', storageError.message);
        }
      }

      const docIds = (documents || []).map(d => d.id);
      if (docIds.length) {
        const { error: deleteDocsError } = await supabase
          .from('documents')
          .delete()
          .in('id', docIds);
        if (deleteDocsError) throw deleteDocsError;
      }

      // Legacy cleanup: if any legacy documents exist with NULL org_id for this user, try to purge them (superadmin only)
      if (isSuperadmin) {
        const { data: legacyDocs, error: legacyFetchErr } = await supabase
          .from('documents')
          .select('id, storage_path')
          .is('org_id', null)
          .eq('user_id', user.id);
        if (!legacyFetchErr && legacyDocs?.length) {
          const legacyPaths = legacyDocs.map(d => d.storage_path).filter(Boolean);
          if (legacyPaths.length) {
            const { error: legacyStorageErr } = await supabase.storage
              .from('invoice-documents')
              .remove(legacyPaths);
            if (legacyStorageErr) console.warn('Legacy storage removal failed:', legacyStorageErr.message);
          }
          const legacyIds = legacyDocs.map(d => d.id);
          const { error: legacyDeleteErr } = await supabase
            .from('documents')
            .delete()
            .in('id', legacyIds);
          if (legacyDeleteErr) console.warn('Legacy documents delete failed:', legacyDeleteErr.message);
        }
      }

      alert('All uploads and invoices for this organization have been deleted.');
      fetchDashboardData();
    } catch (cleanupError) {
      console.error('Cleanup failed:', cleanupError);
      alert('Cleanup failed. Check console for details.');
    } finally {
      setIsCleanupRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
            <p className="text-muted-foreground">
              Overview of invoice processing performance and system metrics
            </p>
          </div>

          {/* Danger Zone for testing */}
          <div className="mb-8 p-4 rounded-lg border border-destructive/40 bg-destructive/5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-destructive uppercase tracking-wide">Testing only</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete all uploaded PDFs, invoices, and processed records for this account.
              </p>
            </div>
            <Button
              variant="destructive"
              size="lg"
              className="w-full md:w-auto text-base px-6 py-4"
              iconName="Trash2"
              iconPosition="left"
              onClick={handleCleanupData}
              disabled={isCleanupRunning}
              loading={isCleanupRunning}
            >
              {isCleanupRunning ? 'Deleting data…' : 'Delete All Test Data'}
            </Button>
          </div>

          {/* Date Range Filter */}
          <DateRangeFilter 
            onDateRangeChange={handleDateRangeChange}
            className="mb-8"
          />

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard {...metrics?.totalUploads} loading={loading} />
            <MetricCard {...metrics?.passRate} loading={loading} />
            <MetricCard {...metrics?.avgConfidence} loading={loading} />
            <MetricCard {...metrics?.timeSaved} loading={loading} />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ProcessingChart
              data={processingData}
              type="bar"
              title="Processing Volume"
              height={300}
            />
            <ProcessingChart
              data={confidenceData}
              type="line"
              title="Confidence Trend"
              height={300}
            />
          </div>

          {/* Activity and Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ActivityFeed activities={activities} loading={loading} />
            </div>
            <div>
              <QuickActions pendingReviews={pendingReviews} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
